import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import api from './client';
import { ENDPOINTS } from './endpoints';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { getResolvedTenantCode } from 'config/resolvedTenant';
import { getTenantCode } from 'config/appConfig';
import { AuthTokens } from 'types/auth';
import { ROUTES } from 'config/routes';
import { tenantStorage } from 'utils/tenantStorage';
import { readTenantFromTokenClaims } from 'utils/jwtClaims';

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const isStoreApiRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/api/store/v1');
};

const isStoreAdminApiRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/api/store/v1/admin/');
};

const isPublicStoreAuthRequest = (url?: string): boolean => {
  if (!url) return false;
  return /\/auth\/(login|register|forgot-password|reset-password|activate|b2b-access-request)/i.test(url);
};

const tokenTenantMatchesResolved = (token: string | null): boolean => {
  if (!token) return true;
  const resolved = getResolvedTenantCode();
  if (!resolved) return true;
  const claims = readTenantFromTokenClaims(token);
  if (!claims) return true;
  return claims.tenantCode.toLowerCase() === resolved.toLowerCase();
};

export const clearSessionAndRedirectToLogin = () => {
  tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
  tenantStorage.removeItem(STORAGE_KEYS.EMAIL);
  tenantStorage.removeItem(STORAGE_KEYS.DISPLAY_NAME);
  delete api.defaults.headers.common.Authorization;
  sessionStorage.setItem('authRedirectReason', 'session_expired');

  if (!window.location.pathname.startsWith(ROUTES.LOGIN)) {
    window.location.replace(ROUTES.LOGIN);
  }
};

export const clearAdminSessionAndRedirect = () => {
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME);
  if (!window.location.pathname.startsWith(ROUTES.ADMIN_LOGIN)) {
    window.location.replace(ROUTES.ADMIN_LOGIN);
  }
};

const handleTenantMismatch = (isAdmin: boolean) => {
  sessionStorage.setItem('authRedirectReason', 'tenant_mismatch');
  if (isAdmin) {
    clearAdminSessionAndRedirect();
  } else {
    clearSessionAndRedirectToLogin();
  }
};

export const setupInterceptors = () => {
  const initialToken = tenantStorage.getItem(STORAGE_KEYS.TOKEN);
  if (initialToken && tokenTenantMatchesResolved(initialToken)) {
    api.defaults.headers.common.Authorization = `Bearer ${initialToken}`;
  } else if (initialToken) {
    tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
    tenantStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    delete api.defaults.headers.common.Authorization;
  }

  api.interceptors.request.use((config) => {
    const tenantCode = getResolvedTenantCode() || getTenantCode();
    if (tenantCode && isStoreApiRequest(config.url)) {
      config.headers['X-Tenant-Code'] = tenantCode;
    }

    if (isStoreAdminApiRequest(config.url)) {
      // Nunca reutilizar JWT de cliente en rutas admin (solo admin token).
      const adminToken = tenantStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
      if (adminToken) {
        if (!tokenTenantMatchesResolved(adminToken)) {
          handleTenantMismatch(true);
          return Promise.reject(new Error('tenant_mismatch'));
        }
        config.headers.Authorization = `Bearer ${adminToken}`;
      } else {
        delete config.headers.Authorization;
      }
    } else if (isPublicStoreAuthRequest(config.url)) {
      // No mandar JWT de cliente en flujos anónimos.
      delete config.headers.Authorization;
    } else {
      const token = tenantStorage.getItem(STORAGE_KEYS.TOKEN);
      if (token) {
        if (!tokenTenantMatchesResolved(token)) {
          handleTenantMismatch(false);
          return Promise.reject(new Error('tenant_mismatch'));
        }
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryConfig | undefined;
      const requestUrl = String(originalRequest?.url || '').toLowerCase();
      const isRefresh = requestUrl.includes('/auth/refresh');
      const isLogin = requestUrl.includes('/auth/login');
      const isPublicAuth = isPublicStoreAuthRequest(originalRequest?.url);
      const isAdminRequest = isStoreAdminApiRequest(originalRequest?.url);

      if (error.response?.status === 503) {
        const data = error.response.data as { code?: string } | undefined;
        if (data?.code === 'store_disabled' && !window.location.pathname.startsWith('/admin')) {
          window.location.replace(ROUTES.STORE_UNAVAILABLE);
        }
      }

      if (error.response?.status === 403) {
        const data = error.response.data as { code?: string } | undefined;
        if (data?.code === 'tenant_mismatch') {
          sessionStorage.setItem('authRedirectReason', 'tenant_mismatch');
          if (isAdminRequest) {
            clearAdminSessionAndRedirect();
          } else {
            clearSessionAndRedirectToLogin();
          }
          return Promise.reject(error);
        }
      }

      if (error.response?.status === 401 && isAdminRequest) {
        clearAdminSessionAndRedirect();
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && isRefresh) {
        clearSessionAndRedirectToLogin();
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && (isLogin || isPublicAuth)) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAdminRequest) {
        const refreshToken = tenantStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) {
          tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
          delete api.defaults.headers.common.Authorization;
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const response = await api.post<AuthTokens>(ENDPOINTS.AUTH_REFRESH, { refreshToken });
          const { token, refreshToken: newRefresh } = response.data;

          tenantStorage.setItem(STORAGE_KEYS.TOKEN, token);
          tenantStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefresh || refreshToken);
          api.defaults.headers.common.Authorization = `Bearer ${token}`;
          originalRequest.headers.Authorization = `Bearer ${token}`;

          return api(originalRequest);
        } catch (refreshError) {
          clearSessionAndRedirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
